import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsentContext } from '../../lib/CookieConsentContext';
import { consumeRevokeNotice } from '../../hooks/useCookieConsent';
import styles from './ConsentRevokeWidget.module.css';

/**
 * Railway-themed widget for revoking cookie consent.
 * Displayed at the bottom of the Cookie Policy page.
 */
export const ConsentRevokeWidget = () => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';
  const { status, revoke, accept } = useCookieConsentContext();
  // Revoking reloads the page when GA was running, so the confirmation survives via sessionStorage.
  const [justRevoked, setJustRevoked] = useState(consumeRevokeNotice);

  const handleRevoke = () => {
    setJustRevoked(true);
    revoke();
  };

  const handleReAccept = () => {
    accept();
    setJustRevoked(false);
  };

  const isActive = status === 'accepted';

  return (
    <div className={styles.widget}>
      {/* Widget header */}
      <div className={styles.header}>
        <span className={styles.headerDot} />
        <span className={styles.headerDot} />
        <span className={styles.headerDot} />
        <span className={styles.headerTitle}>
          {isIt ? 'PANNELLO_CONSENSO_COOKIE.ctrl' : 'COOKIE_CONSENT_PANEL.ctrl'}
        </span>
      </div>

      <div className={styles.body}>
        {/* Track visualization */}
        <div className={styles.trackRow}>
          <svg viewBox="0 0 240 40" className={styles.trackSvg}>
            {/* Main track */}
            <line x1="10" y1="20" x2="230" y2="20" className={styles.rail} />
            {/* Cross-ties */}
            {Array.from({ length: 14 }).map((_, i) => (
              <line
                key={i}
                x1={20 + i * 15}
                y1="14"
                x2={20 + i * 15}
                y2="26"
                className={styles.tie}
              />
            ))}
            {/* Signal */}
            <circle
              cx="20"
              cy="20"
              r="4"
              className={`${styles.signal} ${isActive ? styles.signalActive : styles.signalInactive}`}
            />
            {/* Train dot — only visible when active */}
            {isActive && (
              <g className={styles.trainDot}>
                <circle cx="0" cy="0" r="4" className={styles.trainPulse} />
                <circle cx="0" cy="0" r="2.5" className={styles.trainCore} />
              </g>
            )}
            {/* End signal */}
            <circle
              cx="220"
              cy="20"
              r="4"
              className={`${styles.signal} ${!isActive ? styles.signalInactive : styles.signalDim}`}
            />
          </svg>
        </div>

        {/* Status row */}
        <div className={styles.statusRow}>
          <div className={styles.statusInfo}>
            <span className={styles.statusLabel}>
              {isIt ? 'STATO TELEMETRIA' : 'TELEMETRY STATUS'}
            </span>
            <span className={`${styles.statusValue} ${isActive ? styles.statusActive : styles.statusOff}`}>
              {justRevoked
                ? (isIt ? '✓ CONSENSO REVOCATO' : '✓ CONSENT REVOKED')
                : isActive
                  ? (isIt ? 'ATTIVA — DATI IN TRANSITO' : 'ACTIVE — DATA IN TRANSIT')
                  : (isIt ? 'DISATTIVA — NESSUN TRACCIAMENTO' : 'INACTIVE — NO TRACKING')
              }
            </span>
          </div>
        </div>

        {/* Action */}
        <div className={styles.actionRow}>
          {isActive ? (
            <>
              <p className={styles.desc}>
                {isIt
                  ? 'Hai acconsentito alla raccolta di dati analitici tramite Google Analytics. Puoi revocare il consenso in qualsiasi momento.'
                  : 'You have consented to analytics data collection via Google Analytics. You can revoke consent at any time.'}
              </p>
              <button onClick={handleRevoke} className={styles.revokeBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
                {isIt ? 'Revoca Consenso' : 'Revoke Consent'}
              </button>
            </>
          ) : (
            <>
              <p className={styles.desc}>
                {justRevoked
                  ? (isIt
                      ? 'Il consenso è stato revocato con successo. I cookie analitici sono stati rimossi.'
                      : 'Consent has been successfully revoked. Analytics cookies have been removed.')
                  : (isIt
                      ? 'Non hai fornito il consenso alla raccolta dati analitici. Puoi acconsentire in qualsiasi momento.'
                      : 'You have not consented to analytics data collection. You can consent at any time.')}
              </p>
              <button onClick={handleReAccept} className={styles.acceptBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {isIt ? 'Acconsenti' : 'Accept'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
